import { supabase } from '@/lib/supabase'

// SSOT: chave canônica usada em produção
const KEY_HOME_AS_LOGIN = 'ui.home_redirect_to_login'
const KEY_HOME_PUBLIC_TOKEN = 'ui.home_public_access_token'

// Helper local para detectar ambiente de execução
function isLocalhost(): boolean {
  if (typeof window === 'undefined') return false
  const h = window.location.hostname
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h === '::1' ||
    // suporte a domínios de desenvolvimento locais (ex.: myapp.local)
    h.endsWith('.local')
  )
}

/**
 * Retorna se a rota "/" deve redirecionar para "/login".
 * Regras:
 *  - Produção (não-localhost): usa SSOT em `app_settings(KEY_HOME_AS_LOGIN)`
 *  - Localhost: sempre FALSE por padrão, evitando precisar alternar o switch para testes
 */
export async function getHomeRedirectToLogin(): Promise<boolean> {
  // Em ambiente local, não força Login por padrão (qualquer erro => false)
  if (isLocalhost()) {
    try {
      // Caso no futuro queiram forçar login também no localhost, permitimos um override opcional
      const { data } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', `${KEY_HOME_AS_LOGIN}.local`)
        .maybeSingle()
      const v = (data?.value as string) || 'false'
      return v === 'true'
    } catch {
      return false
    }
  }

  // Produção: comportamento existente (SSOT em app_settings)
  const { data, error } = await supabase
    .from('app_settings')
    .select('value')
    .eq('key', KEY_HOME_AS_LOGIN)
    .maybeSingle()
  if (error) throw error
  const v = (data?.value as string) || 'false'
  return v === 'true'
}

/**
 * Retorna o valor canônico salvo em app_settings, independente do ambiente.
 * Usado pela UI de Settings para refletir o estado persistido (SSOT).
 */
export async function getHomeRedirectToLoginConfig(): Promise<boolean> {
  const { data, error } = await supabase
    .from('app_settings')
    .select('value')
    .eq('key', KEY_HOME_AS_LOGIN)
    .maybeSingle()
  if (error) throw error
  const v = (data?.value as string) || 'false'
  return v === 'true'
}

/**
 * Persiste a configuração canônica (produção).
 * Observação: em localhost o valor salvo continua sendo o de produção,
 * pois o comportamento local tem fallback para FALSE (com override opcional .local).
 */
export async function setHomeRedirectToLogin(on: boolean): Promise<void> {
  const { error } = await supabase
    .from('app_settings')
    .upsert({ key: KEY_HOME_AS_LOGIN, value: on ? 'true' : 'false' }, { onConflict: 'key' })
  if (error) throw error
}

// ---------- Link exclusivo: token público para acessar a Home mesmo com Login ativo ----------

export async function getHomePublicAccessToken(): Promise<string|null> {
  const { data, error } = await supabase
    .from('app_settings')
    .select('value')
    .eq('key', KEY_HOME_PUBLIC_TOKEN)
    .maybeSingle()
  if (error) throw error
  const v = (data?.value as string) || ''
  return v || null
}

export async function setHomePublicAccessToken(token: string): Promise<void> {
  const { error } = await supabase
    .from('app_settings')
    .upsert({ key: KEY_HOME_PUBLIC_TOKEN, value: token }, { onConflict: 'key' })
  if (error) throw error
}

function generateUrlSafeToken(bytes = 16): string {
  try {
    const arr = new Uint8Array(bytes)
    // @ts-ignore
    ;(typeof crypto !== 'undefined' ? crypto : (window as any).crypto).getRandomValues(arr)
    // Hex string é URL-safe e suficiente para identificação opaca
    return Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('')
  } catch {
    return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)
  }
}

export async function regenerateHomePublicAccessToken(): Promise<string> {
  const token = generateUrlSafeToken(24)
  await setHomePublicAccessToken(token)
  return token
}

export async function verifyHomePublicAccessToken(candidate: string | null | undefined): Promise<boolean> {
  if (!candidate) return false
  try {
    const cur = await getHomePublicAccessToken()
    if (!cur) return false
    return String(candidate) === String(cur)
  } catch {
    return false
  }
}

const SS_SESSION_KEY = 'ui.public_access_session'

export function enableHomePublicAccessSession(): void {
  try { sessionStorage.setItem(SS_SESSION_KEY, '1') } catch {}
}

export function hasHomePublicAccessSession(): boolean {
  try { return sessionStorage.getItem(SS_SESSION_KEY) === '1' } catch { return false }
}
